import { getClosestLayerFn } from 'uScope/js/lib/services/rendering/ViewportTileLayer';
import * as util from './markup-utils';
import {BoundingBox} from '../../../lib/objects/BoundingBox';

/**
 * Generates a "Large" region, as suggested by the region.isSmall property. This factory creates an object that will render
 * tiles for an analyzed markup region. It is expected that it will `paint()` on every zoom and pan event on the current
 * viewport (the MarkupService takes care of the binding for all region types). The returned object has three properties:
 *   - fragment: An HTML fragment containing the tiles to add to the DOM
 *   - render: a method that generates the initial set of tiles for all zoom levels
 *   - paint: a method that will choose the appropriate set of tiles (based on current zoom level), add or remove them
 *            from the DOM as needed, and update their position and sizes
 * TODO: Much of this logic already exists in uScope's rendering function. It would be more ideal to reuse that logic
 * TODO: than to duplicate it here
 * @param region
 * @param viewport
 * @returns {{render: (function()), paint: (function()), fragment}}
 */
export const createLargeRegion = (region, viewport) => {
  let fragment;
  // The layerContainer is an anchor that allows us to add / remove tiles for this region
  let layerContainer;
  let viewportState;
  let urlBuilder;
  // regionBounds defines the boundaries of the region. Useful for determining whether or not the region is visible
  // in the viewport; if it's not then we just hide all the tiles.
  let regionBounds;
  // Each time we paint, the first thing we do is see if the region is visible, and hide / show layerContainer as needed.
  // However, always showing/hiding the element on each paint can be expensive, so we don't want to do that, and querying
  // the element's CSS properties is also expensive, so we introduce an "shouldRender" flag that lets us track if the
  // region is hidden without resorting to either of these techniques
  let shouldRender = true;
  // Not to be confused with shouldRender; isRegionHidden is used to let users control over whether or not the region
  // is visible.
  let isRegionHidden = true;
  // "layers" is a container to track all elements at any given zoom, indexed by zoom level (e.g. let level = levels[zoom]);
  const layers = {};
  // activeLayer helps us determine if we need to remove all tiles. If the requested zoom won't be represented by the
  // activeLayer then we hide it and switch to a different layer.
  let activeLayer;
  let getClosestLayer;
  let viewportZoom;

  // Show/Hide region methods control the outer container containing the tiles. On hide, all tiles are removed from
  // the DOM as a performance enhancement (no need for the browser to paint these things if they're not being displayed)
  const showRegion = () => {
    layerContainer.style.display = '';
    shouldRender = false;
  };
  const hideRegion = () => {
    layerContainer.style.display = 'none';
    hideAllTiles(activeLayer);
    shouldRender = true;
  };

    // As the name suggests it hides all tiles. Note this makes use of Element.remove(), which is not supported in IE,
    // but which is polyfilled in browser-compatibility.js
  const hideAllTiles = layer => layer && layer.tiles.forEach(tile => tile.remove());

  // Called only once per layer, createTiles() will generate all tiles that can be rendered for a particular layer.
  const createTiles = (layer) => {
    const layerZoom = layer.zoom;
    const tiles = layer.tiles;

    const markupInfo = region.getMarkupInfo();
    const tileWidth = markupInfo.tileWidth;
    const tileHeight = markupInfo.tileHeight;
    const scaledRegionBottom = regionBounds.top + regionBounds.h / layerZoom;
    let tileCoordX = 0;
    let tileCoordY = 0;

    for (let tileTop = regionBounds.top; tileTop < scaledRegionBottom; tileTop += tileHeight, tileCoordY++) {
      // X coord is always x when starting a new row (it gets incremented in the inner loop)
      tileCoordX = 0;
      // markup based coord of tile top; first tile is at 0,0
      const tileY = tileTop - regionBounds.top;
      // height of to be requested tile, clipping at bottom
      const tileH = ((tileTop + tileHeight) > scaledRegionBottom) ? scaledRegionBottom - tileTop : tileHeight;
      const regRight = regionBounds.left + regionBounds.w / layerZoom;

      for (let tileLeft = regionBounds.left; tileLeft < regRight; tileLeft += tileWidth, tileCoordX++) {
        // markup based coord of tile left; first tile is at 0,0
        const tileX = tileLeft - regionBounds.left;
        // width of to be requested tile, clipping at right
        const tileW = ((tileLeft + tileWidth) > regRight) ? regRight - tileLeft : tileWidth;

        const tileSrc = urlBuilder.region(tileX, tileY, tileW, tileH, layerZoom, util.MARKUP_QUALITY);

        const tile = document.createElement('img');
        tile.className = 'us-markup-tile';
        tile.setAttribute('img-source', tileSrc);

        // Useful for debugging; adds position info to the elements in the DOM.
        // tile.setAttribute('tileCoordX', tileCoordX);
        // tile.setAttribute('tileCoordY', tileCoordY);
        // tile.setAttribute('zoom', layerZoom);

        // Track these items later when the tile gets rendered (in paint())
        tile.renderProps = {
          tileX,
          tileY,
          tileW,
          tileH,
          bounds: createTileBoundingBox(
            regionBounds,
            tileX * layerZoom,
            tileY * layerZoom,
            tileW * layerZoom,
            tileH * layerZoom)
        };

        tiles.push(tile);
      }
    }
  };

    // Called each time the viewport is panned or zoomed (assuming the region is visible)
  const paintLayer = (layer, zoomChanged) => {
    const viewportBounds = viewport.getVPViewRect2();
    const zoomOfRequest = layer.zoom;

    const tiles = layer.tiles;

    for (let i = 0; i < tiles.length; i++) {
      const tile = tiles[i];
      const {tileX, tileY, tileW, tileH, bounds} = tile.renderProps;

      if (zoomChanged) {
        const viewZoom = viewportState.zoom;
        const zoomFactor = viewZoom / zoomOfRequest;

        const xPosVP = regionBounds.left + tileX * zoomOfRequest;
        const yPosVP = regionBounds.top + tileY * zoomOfRequest;
        const left = xPosVP / viewZoom;
        const top = yPosVP / viewZoom;
        const width = tileW / zoomFactor;
        const height = tileH / zoomFactor;

        tile.style.width = width + 'px';
        tile.style.height = height + 'px';
        tile.style.transform = `translate(${left}px, ${top}px)`;
      }

      if (bounds.hasOverlap(viewportBounds)) {
        // Note that attaching `src` will fire off an HTTP request for the tile. We only want to do this if the
        // tile is visible, particularly for large regions with potentially thousands of tiles.
        if (!tile.src) {
          tile.src = tile.getAttribute('img-source');
        }
        layerContainer.appendChild(tile);
      } else {
        tile.remove();
      }
    }
  };

  return {
    render () {
      urlBuilder = util.createUrlBuilder(viewport, region.MarkupImageId);
      regionBounds = region.getMarkupInfo().boundingBox;
      viewportState = viewport.getVPView();
      fragment = document.createDocumentFragment();
      layerContainer = document.createElement('div');
      fragment.appendChild(layerContainer);

      viewport.getPyramid().forEach(layer => {
        const tiles = [];
        layers[layer.zoom] = {
          tiles,
          zoom: layer.zoom
        };
      });

      getClosestLayer = getClosestLayerFn(layers);
      hideRegion();
    },

    paint () {
      if (!isRegionHidden && util.isVisible(region, viewport)) {
        if (shouldRender) {
          showRegion();
        }
      } else {
        if (!shouldRender) {
          hideRegion();
        }
        // If the region is not visible just exit the paint function
        return;
      }
      // First check if zoom has changed; if so we have to do a little more work.
      let zoomChanged = false;

      if (viewportZoom !== viewportState.zoom) {
        zoomChanged = true;
        viewportZoom = viewportState.zoom;

        // If we're switching zooms we need to hide the currently active layer
        hideAllTiles(activeLayer);
        activeLayer = getClosestLayer(viewportState.zoom);
      }

      // If this layer hasn't been painted before we need to create the tile map
      if (!activeLayer.tiles.length) {
        createTiles(activeLayer);
      }

      paintLayer(activeLayer, zoomChanged);
    },

    show () {
      isRegionHidden = false;
      this.paint();
    },

    hide () {
      isRegionHidden = true;
      this.paint();
    },

    remove () {
      return layerContainer.remove();
    },

    markAsDirty () {
      // Not implemented. This function should flag this region as "dirty", which should tell the render cycle that
      // the markupImageId attribute of the region has changed and we should request a new one. This is not trivial,
      // but the negative effects of not doing it are minor: markupImageId is changed when a non-incremental macro
      // is run on a layer. The inputs to the macro (image area) are the same, so the output image should be identical.
    },

    get fragment () {
      return fragment;
    }
  };
};

/**
 * Creates a bounding box for the tile. This will allow us to check if an individual tile is on the screen
 * @param container
 * @param l
 * @param t
 * @param w
 * @param h
 * @returns {BoundingBox}
 */
const createTileBoundingBox = (container, l, t, w, h) => {
  return new BoundingBox(
    container.left + l,
    container.top + t,
    container.left + l + w,
    container.top + t + h
  );
};



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/image-analysis/markup/large-markup-region.js